import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';

/**
 * <offsure-gallery>
 * PDP gallery: thumbnail rail + stacked media column + mobile swipe counter +
 * tap-to-zoom dialog with pinch/pan + colorway crossfade.
 *
 * The rail/gal markup is rendered server-side by the calling section's
 * media-ledger governance loop (see offsure-pdp.liquid / offsure-pdp-v2.liquid
 * and snippets/offsure-gallery.liquid) — this component never invents or
 * re-derives which media is allowed; on a colorway change it only replays the
 * same eligible-media list the section already computed and embedded as
 * `#opd-media-{sid}` JSON (itself filtered server-side by the ledger).
 *
 * Public API (called by the section's swatch-click handler):
 *   - setHeroImage(src, alt)   swap the lead image (non-grouped / legacy products)
 *   - renderForColor(color)    rebuild rail + stack for a colorway (governed products)
 */
export default class OffsureGallery extends Component {
  requiredRefs = ['zoomDialog', 'zoomImage', 'zoomStage', 'zoomClose', 'zoomPrev', 'zoomNext', 'zoomCounter'];

  /** @type {{id:string,type:'image'|'video',color:string,slot:string,qa:string,src:string,thumb:string,alt:string,sources?:{src:string,type:string}[]}[]} */
  #mediaItems = [];

  /** @type {{id:string,color:string,size:string,available:boolean}[]} */
  #variants = [];

  /** @type {HTMLElement | null} */
  #hero = null;

  /** @type {number} */
  #zoomIndex = 0;

  #galleryAnimation = null;

  #resizeObserver = new ResizeObserver(debounce(() => this.#syncCounterFromScroll(), 100));

  connectedCallback() {
    super.connectedCallback();

    this.sid = this.dataset.sid || '';
    this.mediaGrouped = this.dataset.mediaGrouped === 'true';
    this.productTitle = this.dataset.productTitle || '';
    // "zoom only" mode: the host page (e.g. offsure-pdp-v2.liquid) already
    // owns a working rail/dots/counter/colorway-rebuild implementation of
    // its own. This component then only adds the tap-to-zoom dialog with
    // pinch/pan on top, and touches nothing else, so it can't double-bind
    // or fight the host's existing gallery JS.
    this.zoomOnly = this.dataset.zoomOnly === 'true';

    this.rail = this.querySelector('[data-opd-rail]');
    this.viewport = this.querySelector('[data-opd-gal-viewport]');
    this.gal = this.querySelector('[data-opd-gal]');
    this.dotsWrap = this.querySelector('[data-opd-dots]');
    this.counterEl = this.querySelector('[data-opd-gal-counter]');
    this.counterCurrent = this.querySelector('[data-opd-gal-counter-current]');
    this.counterTotal = this.querySelector('[data-opd-gal-counter-total]');
    this.#hero = this.querySelector('[data-opd-hero]');

    this.#markLoaded();
    this.#bindZoomTriggers();
    this.#bindZoomControls();
    this.#bindZoomGestures();

    if (!this.zoomOnly) {
      this.#loadMediaJson();
      this.#loadVariantsJson();
      this.#bindRail();
      this.#buildDots();
      this.#bindViewportScroll();
      if (this.viewport) this.#resizeObserver.observe(this.viewport);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#galleryAnimation?.cancel();
  }

  #loadMediaJson() {
    const script = document.getElementById(`opd-media-${this.sid}`);
    if (!script) return;
    try {
      this.#mediaItems = JSON.parse(script.textContent || '[]') || [];
    } catch {
      this.#mediaItems = [];
    }
  }

  #loadVariantsJson() {
    const script = document.getElementById(`opd-variants-${this.sid}`);
    if (!script) return;
    try {
      this.#variants = JSON.parse(script.textContent || '[]') || [];
    } catch {
      this.#variants = [];
    }
  }

  /** Fade-in the initial server-rendered images/thumbs once loaded (cream skeleton until then). */
  #markLoaded() {
    this.querySelectorAll('.opd-shot img, .opd-shot__video, .opd-rail__t img').forEach((el) => {
      const mark = () => el.classList.add('opd-loaded');
      if (el instanceof HTMLImageElement) {
        if (el.complete) mark();
        else el.addEventListener('load', mark, { once: true });
      } else {
        mark();
      }
    });
  }

  static #handleize(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  #groupedItemsFor(color) {
    const slug = OffsureGallery.#handleize(color);
    // Governance gate is untouched: "own"/"ownPhysical"/hasMediaForColor() below only ever look at
    // qa 'physical' or 'approved-ai' — exactly the same set as before. 'supplement' and 'ai-blank'
    // images (theme-side, dev-theme only) are pulled in separately, purely for display, and can never
    // make a color "available" on their own.
    const eligible = this.#mediaItems.filter((m) => m && (m.qa === 'physical' || m.qa === 'approved-ai'));
    const ownPhysical = eligible.filter((m) => m.color === slug && m.qa === 'physical');
    const own = eligible.filter((m) => m.color === slug);
    // Approved-AI media supplements a physical record, except under the
    // 'physical-and-human-approved-ai' product policy where the owner has approved an AI-only
    // colorway (2026-09-04, White Beech): then approved-ai frames alone make the color available.
    const aiOnlyAllowed = this.dataset.mediaPolicy === 'physical-and-human-approved-ai'
      && !ownPhysical.length && own.some((m) => m.qa === 'approved-ai');
    if (!ownPhysical.length && !aiOnlyAllowed) return [];
    const shared = eligible.filter((m) => m.color === 'shared' && m.qa === 'physical');
    const bySlot = (a, b) => String(a.slot || '').localeCompare(String(b.slot || ''), undefined, { numeric: true });
    own.sort(bySlot);
    shared.sort(bySlot);
    // Keep every governed photo that merchandising attached to the selected color. The first image
    // remains the lead and a true back view is promoted to second when necessary, but later lifestyle
    // and detail frames must not be dropped from the storefront gallery.
    //
    // Back-priority fix (2026-09-08): a plain slice(0,3) can bury a genuine worn-back shot past the
    // cap when an extra near-duplicate front pose sits ahead of it in slot order (OC203 Navy Coast:
    // slot:02 is a front-angle repeat, slot:04 is the "FREE AS THE OCEAN" back). #isBackShot below
    // flags a true back/rear shot by its alt text (excluding "front ..." pose descriptors, which can
    // still mention a rear-visible detail like a hood patch without being a back view). Only fires
    // when the back exists but was excluded by the cap (backIdx > 2) — every colorway whose back
    // already lands in the natural top-3 is untouched, byte-for-byte. Governance ("own"/eligible
    // above) is unchanged; this only reorders which already-approved ledger items compose the triple.
    let ledgerSet;
    if (aiOnlyAllowed) {
      ledgerSet = own;
    } else {
      const backIdx = own.findIndex((m) => OffsureGallery.#isBackShot(m.alt));
      if (backIdx > 1) {
        const front = own[0];
        const back = own[backIdx];
        const rest = own.filter((_, i) => i !== 0 && i !== backIdx);
        ledgerSet = [front, back, ...rest].filter(Boolean);
      } else {
        ledgerSet = own;
      }
    }
    // Tail — slots 4+, outside the SHA ledger: the theme-side supplement list for this colorway, in
    // its curated order (lifestyle solo, group, pairing-with-matching-piece). The old blank-front/
    // blank-back tail slots are gone now that assets/oc-blank-*.jpg is retired (offsure-blanks-enabled
    // snippet) — supplement items flow straight into those freed positions instead of leaving a gap.
    const supplement = this.#mediaItems.filter((m) => m && m.qa === 'supplement' && m.color === slug);
    const tail = supplement.filter(Boolean);
    return ledgerSet.concat(shared).concat(tail);
  }

  /** Alt text reads as a stated front-facing pose ("...in Color, front angle/view/look/full/silhouette"). */
  static #frontPoseComma = /,\s*front[\s-]+(view|angle|look|full|silhouette)/i;

  /**
   * Alt text reads as an artwork/fabric detail shot. Deliberately excludes "print", "zip" and
   * "patch" — each collides with a product name itself ("Print Pullover", "Full-Zip Hoodie",
   * "Patch Pullover"), which would flavor every alt for that product and defeat the tiering below.
   * Every real detail shot in the current copy also carries "artwork" or "embroidery", so nothing
   * is lost.
   */
  static #detailFlavor = /artwork|embroidery|detail|fabric|denim|logo/i;

  /**
   * True when an alt string describes a genuine worn-back/rear view rather than a front-facing shot
   * that merely mentions a rear-visible detail (e.g. "front angle with rear-hood patch visible").
   * Checked against the full alt (not just a "descriptor" tail) since description-led alts put the
   * back signal up front, e.g. "FREE AS THE OCEAN upper-back artwork on the ... in Navy Coast,
   * contextual view".
   * @param {string} alt
   * @returns {boolean}
   */
  static #isBackShot(alt) {
    const str = String(alt || '');
    if (OffsureGallery.#frontPoseComma.test(str)) return false;
    return /\b(rear|back)\b/i.test(str);
  }

  /**
   * Whether the ledger-filtered media list has at least one item for this
   * colorway. Used by the section's variant-availability check so it never
   * enables Add to bag for a color with no approved photography.
   * @param {string} color
   * @returns {boolean}
   */
  hasMediaForColor(color) {
    if (this.zoomOnly) return true;
    return this.#groupedItemsFor(color).length > 0;
  }

  /**
   * True when the color is shown on owner-approved AI frames only (no physical
   * ledger frame). Drives the "photos are being shot" notice on the PDP. 2026-09-08.
   * @param {string} color
   * @returns {boolean}
   */
  isAiOnlyColor(color) {
    if (this.zoomOnly) return false;
    const slug = OffsureGallery.#handleize(color);
    const own = this.#mediaItems.filter((m) => m && m.color === slug && (m.qa === 'physical' || m.qa === 'approved-ai'));
    return own.length > 0 && !own.some((m) => m.qa === 'physical');
  }

  /**
   * Swaps the lead/hero image (legacy, non-grouped products). Governed
   * (media-ledger) products use renderForColor() instead, which rebuilds the
   * whole stack rather than swapping a single image.
   * @param {string} img
   * @param {string} [alt]
   */
  setHeroImage(img, alt) {
    if (!img || this.zoomOnly) return;
    const apply = () => {
      if (this.#hero && img) {
        if (this.#hero instanceof HTMLImageElement) this.#hero.src = img;
        if (alt) this.#hero.setAttribute('alt', alt);
      }
      const stickyThumb = document.querySelector(`#opd-sticky-${this.sid} [data-opd-sticky-thumb]`);
      if (stickyThumb instanceof HTMLImageElement) {
        stickyThumb.src = img;
        stickyThumb.hidden = false;
      }
      if (this.rail) {
        let matched = false;
        this.rail.querySelectorAll('.opd-rail__t').forEach((t) => {
          const on = t.getAttribute('data-rail-img') === img;
          t.classList.toggle('on', on);
          t.setAttribute('aria-pressed', String(on));
          if (on) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current');
          if (on) matched = true;
        });
        if (!matched) this.rail.querySelectorAll('.opd-rail__t').forEach((t) => t.classList.remove('on'));
      }
      if (this.closest('[data-layout="carousel"]') && this.gal?.firstElementChild) {
        try {
          this.gal.scrollTo({ left: 0, behavior: 'auto' });
        } catch {
          this.gal.scrollLeft = 0;
        }
      }
    };

    this.#updateWithFade(apply);
  }

  /**
   * Rebuilds the rail + stacked media column for a newly selected colorway,
   * from the same ledger-filtered media list the section rendered
   * server-side (`#opd-media-{sid}`) — never a client-invented list.
   * @param {string} color
   */
  renderForColor(color) {
    if (this.zoomOnly || !this.mediaGrouped || !this.rail || !this.gal) return;

    const items = this.#groupedItemsFor(color);
    const realWrap = document.querySelector('[data-opd-real]');

    const rebuild = () => {
      this.rail.replaceChildren();
      this.gal.replaceChildren();
      if (this.dotsWrap) this.dotsWrap.replaceChildren();

      if (!items.length) {
        const missing = document.createElement('div');
        missing.className = 'opd-shot opd-shot--missing';
        missing.setAttribute('data-opd-media-missing', '');
        missing.textContent = `Physical product photography for ${color} is being prepared.`;
        this.gal.appendChild(missing);
        this.#hero = null;
        const stickyThumb = document.querySelector(`#opd-sticky-${this.sid} [data-opd-sticky-thumb]`);
        if (stickyThumb instanceof HTMLImageElement) stickyThumb.hidden = true;
        this.#buildDots();
        this.#syncCounterFromScroll();
        return;
      }

      items.forEach((item, index) => {
        const thumb = document.createElement('button');
        thumb.type = 'button';
        thumb.className = `opd-rail__t${index === 0 ? ' on' : ''}`;
        thumb.setAttribute('data-rail-img', item.src);
        thumb.setAttribute('data-rail-alt', item.alt || this.productTitle);
        thumb.setAttribute('data-media-qa', item.qa || '');
        thumb.setAttribute('data-media-type', item.type || 'image');
        thumb.setAttribute('aria-label', item.alt || this.productTitle);
        thumb.setAttribute('aria-pressed', String(index === 0));
        if (index === 0) thumb.setAttribute('aria-current', 'true');
        const thumbImg = document.createElement('img');
        thumbImg.src = item.thumb;
        thumbImg.alt = '';
        thumbImg.width = 200;
        thumbImg.height = 250;
        thumbImg.loading = 'lazy';
        if (item.objectPosition) thumbImg.style.objectPosition = item.objectPosition;
        thumb.appendChild(thumbImg);
        if (item.type === 'video') {
          const play = document.createElement('span');
          play.className = 'opd-rail__play';
          play.setAttribute('aria-hidden', 'true');
          thumb.appendChild(play);
        }
        this.rail.appendChild(thumb);

        const shot = document.createElement('div');
        shot.className = `opd-shot${item.type === 'video' ? ' opd-shot--video' : ''}`;
        if (item.id != null) shot.setAttribute('data-media-id', item.id);
        shot.setAttribute('data-media-qa', item.qa || '');
        shot.setAttribute('data-media-type', item.type || 'image');

        if (item.type === 'video' && item.sources?.length) {
          const video = document.createElement('video');
          video.className = 'opd-shot__video opd-loaded';
          video.controls = true;
          video.muted = true;
          video.playsInline = true;
          video.poster = item.src;
          if (index === 0) {
            video.id = `opd-hero-${this.sid}`;
          }
          for (const source of item.sources) {
            const sourceEl = document.createElement('source');
            sourceEl.src = source.src;
            sourceEl.type = source.type;
            video.appendChild(sourceEl);
          }
          shot.appendChild(video);
        } else {
          const image = document.createElement('img');
          image.src = item.src1200 || item.src;
          if (item.src800 && item.src1200 && item.src1600) {
            image.srcset = `${item.src800} 800w, ${item.src1200} 1200w, ${item.src1600} 1600w`;
            image.sizes = '(max-width: 900px) 87vw, 590px';
          }
          image.alt = item.alt || this.productTitle;
          image.width = 1100;
          image.height = 1375;
          if (item.objectPosition) image.style.objectPosition = item.objectPosition;
          if (index === 0) {
            image.id = `opd-hero-${this.sid}`;
            image.setAttribute('data-opd-hero', '');
          }
          // Only the first two slides (worn-front + AI-blank, when present) load eager — matches the
          // server-rendered initial gallery's loading strategy.
          if (index <= 1) {
            image.loading = 'eager';
            image.fetchPriority = 'high';
          } else {
            image.loading = 'lazy';
          }
          shot.appendChild(image);
        }

        if (index === 0) {
          const inStock = this.#variants.some((v) => v.color === color && v.available);
          const tag = document.createElement('span');
          tag.className = 'opd-shot__tag opd-meta';
          tag.textContent = inStock ? 'In stock' : 'Sold out';
          shot.appendChild(tag);
        }
        this.gal.appendChild(shot);
      });

      this.#hero = this.gal.querySelector('[data-opd-hero]');
      if (realWrap) realWrap.setAttribute('data-selected-color', color);
      const stickyThumb = document.querySelector(`#opd-sticky-${this.sid} [data-opd-sticky-thumb]`);
      if (stickyThumb instanceof HTMLImageElement) {
        stickyThumb.src = items[0].src;
        stickyThumb.hidden = false;
      }
      this.#markLoaded();
      this.#bindRail();
      this.#buildDots();
      try {
        this.gal.scrollTo({ left: 0, behavior: 'auto' });
      } catch {
        this.gal.scrollLeft = 0;
      }
      if (this.viewport) this.viewport.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      this.#syncCounterFromScroll();
    };

    this.#updateWithFade(rebuild);
  }

  /**
   * Fade within the gallery's own layer. Document view transitions put the
   * gallery snapshot above the fixed header, irrespective of its z-index.
   * @param {() => void} update
   */
  #updateWithFade(update) {
    this.#galleryAnimation?.cancel();
    update();
    if (this.gal && !prefersReducedMotion()) {
      this.#galleryAnimation = this.gal.animate([{ opacity: 0.65 }, { opacity: 1 }], {
        duration: 180,
        easing: 'ease-out',
      });
    }
  }

  // ---- rail ----
  #bindRail() {
    if (!this.rail) return;
    this.rail.querySelectorAll('.opd-rail__t').forEach((t) => {
      if (t.getAttribute('data-opd-bound') === 'true') return;
      t.setAttribute('data-opd-bound', 'true');
      t.addEventListener('click', () => {
        const img = t.getAttribute('data-rail-img');
        // Rail and slides share the same governed order. Do not match CDN
        // URLs: responsive images have different width parameters after a
        // color change, and a failed match used to replace the first image.
        const index = Array.from(this.rail.querySelectorAll('.opd-rail__t')).indexOf(t);
        const targetShot = this.gal?.querySelectorAll('.opd-shot')[index];
        if (targetShot) {
          const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
          if (getComputedStyle(this.gal).scrollSnapType !== 'none') {
            this.gal.scrollTo({ left: targetShot.offsetLeft - this.gal.offsetLeft, behavior });
          } else {
            const header = document.querySelector('#header-group [id^="ohd-"]');
            const clearance = (header?.getBoundingClientRect().height || 0) + 16;
            window.scrollTo({ top: window.scrollY + targetShot.getBoundingClientRect().top - clearance, behavior });
          }
          this.rail.querySelectorAll('.opd-rail__t').forEach((x) => {
            const on = x === t;
            x.classList.toggle('on', on);
            x.setAttribute('aria-pressed', String(on));
            if (on) x.setAttribute('aria-current', 'true'); else x.removeAttribute('aria-current');
          });
        } else if (img && !this.mediaGrouped) {
          this.setHeroImage(img, t.getAttribute('data-rail-alt') || undefined);
        }
      });
    });
  }

  // ---- carousel dots (carousel layout only; CSS-hidden otherwise) ----
  #buildDots() {
    if (!this.dotsWrap || !this.gal) return;
    this.dotsWrap.replaceChildren();
    if (this.gal._opdScrollHandler) this.gal.removeEventListener('scroll', this.gal._opdScrollHandler);
    const slides = Array.from(this.gal.querySelectorAll('.opd-shot'));
    if (slides.length <= 1) return;

    slides.forEach((s, i) => {
      const d = document.createElement('button');
      d.type = 'button';
      d.setAttribute('aria-label', `Go to image ${i + 1}`);
      d.setAttribute('aria-pressed', i === 0 ? 'true' : 'false');
      if (i === 0) d.className = 'on';
      d.addEventListener('click', () => {
        try {
          this.gal.scrollTo({ left: s.offsetLeft - this.gal.offsetLeft, behavior: 'smooth' });
        } catch {
          this.gal.scrollLeft = s.offsetLeft - this.gal.offsetLeft;
        }
      });
      this.dotsWrap.appendChild(d);
    });

    const dotEls = Array.from(this.dotsWrap.querySelectorAll('button'));
    let rafPending = false;
    this.gal._opdScrollHandler = () => {
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(() => {
        rafPending = false;
        const w = (slides[1] ? slides[1].offsetLeft - slides[0].offsetLeft : this.gal.clientWidth) || 1;
        let idx = Math.round(this.gal.scrollLeft / w);
        idx = Math.max(0, Math.min(idx, dotEls.length - 1));
        dotEls.forEach((d, i) => {
          const selected = i === idx;
          d.classList.toggle('on', selected);
          d.setAttribute('aria-pressed', String(selected));
        });
      });
    };
    this.gal.addEventListener('scroll', this.gal._opdScrollHandler, { passive: true });
  }

  // ---- mobile swipe counter ----
  #bindViewportScroll() {
    if (!this.gal) return;
    this.gal.addEventListener('scroll', () => this.#syncCounterFromScroll(), { passive: true });
    this.#syncCounterFromScroll();
  }

  #syncCounterFromScroll() {
    if (!this.counterEl || !this.gal) return;
    const slides = Array.from(this.gal.querySelectorAll('.opd-shot'));
    if (slides.length <= 1) {
      this.counterEl.hidden = true;
      return;
    }
    this.counterEl.hidden = false;
    if (this.counterTotal) this.counterTotal.textContent = String(slides.length);

    // Only meaningful once the gallery is a horizontal snap-scroller (mobile CSS).
    const isSwipeMode = getComputedStyle(this.gal).scrollSnapType !== 'none';
    let index = 0;
    if (isSwipeMode) {
      const w = (slides[1]?.offsetLeft ?? 0) - (slides[0]?.offsetLeft ?? 0) || this.gal.clientWidth || 1;
      index = Math.round(this.gal.scrollLeft / w);
    } else {
      // Vertical stack (desktop / editorial): find the most-visible shot.
      const viewportRect = this.gal.getBoundingClientRect();
      let best = { i: 0, visible: -Infinity };
      slides.forEach((slide, i) => {
        const r = slide.getBoundingClientRect();
        const visible = Math.min(r.bottom, viewportRect.bottom) - Math.max(r.top, viewportRect.top);
        if (visible > best.visible) best = { i, visible };
      });
      index = best.i;
    }
    index = Math.max(0, Math.min(index, slides.length - 1));
    if (this.counterCurrent) this.counterCurrent.textContent = String(index + 1);
  }

  // ---- zoom dialog ----

  /** @type {HTMLElement | null} */
  #zoomOpener = null;

  #bindZoomTriggers() {
    if (!this.gal) return;
    this.gal.addEventListener('click', (event) => {
      const shot = /** @type {HTMLElement | null} */ (event.target instanceof Element ? event.target.closest('.opd-shot') : null);
      if (!shot || shot.hasAttribute('data-opd-media-missing')) return;
      if (event.target instanceof HTMLElement && event.target.closest('video')) return; // let native video controls work
      const slides = Array.from(this.gal.querySelectorAll('.opd-shot'));
      const index = slides.indexOf(shot);
      if (index === -1) return;
      this.openZoom(index, shot);
    });
  }

  #zoomItems() {
    if (!this.gal) return [];
    return Array.from(this.gal.querySelectorAll('.opd-shot')).map((shot) => {
      const img = shot.querySelector('img');
      const video = shot.querySelector('video');
      return {
        type: video ? 'video' : 'image',
        src: video ? video.currentSrc || video.querySelector('source')?.getAttribute('src') || '' : img?.getAttribute('src') || '',
        alt: img?.getAttribute('alt') || this.productTitle,
      };
    });
  }

  /**
   * Opens the zoom dialog.
   * @param {number} index
   * @param {HTMLElement} [opener] - The element that triggered the open, so
   *   focus can return to it when the dialog closes (dialog focus-trap
   *   hygiene: a keyboard/screen-reader user should never lose their place).
   *   Falls back to whatever currently has focus.
   */
  openZoom(index, opener) {
    const items = this.#zoomItems();
    if (!items.length) return;
    this.#zoomOpener = opener || /** @type {HTMLElement | null} */ (document.activeElement);
    this.#zoomIndex = Math.max(0, Math.min(index, items.length - 1));
    this.#renderZoom();
    if (!this.refs.zoomDialog.open) this.refs.zoomDialog.showModal();
    document.addEventListener('keydown', this.#handleZoomKeydown);
  }

  close() {
    if (this.refs.zoomDialog.open) this.refs.zoomDialog.close();
    document.removeEventListener('keydown', this.#handleZoomKeydown);
    this.#resetZoomTransform();
    this.#restoreFocusToOpener();
  }

  /** Returns focus to whatever opened the zoom dialog, then forgets it. */
  #restoreFocusToOpener() {
    const opener = this.#zoomOpener;
    this.#zoomOpener = null;
    if (!opener || !opener.isConnected) return;
    // .opd-shot (and most zoom triggers) aren't natively focusable; make it
    // programmatically focusable so it can receive focus back. Removing the
    // tabindex synchronously right after focus() would un-focus the element
    // immediately (a focused-but-unfocusable element gets kicked back to
    // <body> by the browser) — so the cleanup waits for the element to
    // naturally blur instead of happening in the same tick.
    const hadTabIndex = opener.hasAttribute('tabindex');
    if (!hadTabIndex) {
      opener.setAttribute('tabindex', '-1');
      opener.addEventListener(
        'blur',
        () => opener.removeAttribute('tabindex'),
        { once: true }
      );
    }
    opener.focus({ preventScroll: true });
  }

  prev() {
    const items = this.#zoomItems();
    if (!items.length) return;
    this.#zoomIndex = (this.#zoomIndex - 1 + items.length) % items.length;
    this.#renderZoom();
  }

  next() {
    const items = this.#zoomItems();
    if (!items.length) return;
    this.#zoomIndex = (this.#zoomIndex + 1) % items.length;
    this.#renderZoom();
  }

  #renderZoom() {
    const items = this.#zoomItems();
    const item = items[this.#zoomIndex];
    if (!item) return;
    this.refs.zoomImage.src = item.src;
    this.refs.zoomImage.alt = item.alt;
    this.refs.zoomCounter.textContent = `${this.#zoomIndex + 1} / ${items.length}`;
    this.refs.zoomPrev.hidden = items.length <= 1;
    this.refs.zoomNext.hidden = items.length <= 1;
    this.#resetZoomTransform();
  }

  #handleZoomKeydown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    } else if (event.key === 'ArrowLeft') {
      this.prev();
    } else if (event.key === 'ArrowRight') {
      this.next();
    }
  };

  #bindZoomControls() {
    // Native <dialog> already closes on backdrop click when we listen for it explicitly.
    this.refs.zoomDialog.addEventListener('click', (event) => {
      if (event.target === this.refs.zoomDialog) this.close();
    });
    this.refs.zoomDialog.addEventListener('close', () => {
      document.removeEventListener('keydown', this.#handleZoomKeydown);
      this.#resetZoomTransform();
      this.#restoreFocusToOpener();
    });
  }

  // ---- pinch / pan ----
  #zoomScale = 1;
  #zoomX = 0;
  #zoomY = 0;
  #pointers = new Map();
  #pinchStartDistance = 0;
  #pinchStartScale = 1;
  #panStart = null;

  #resetZoomTransform() {
    this.#zoomScale = 1;
    this.#zoomX = 0;
    this.#zoomY = 0;
    this.#applyZoomTransform();
  }

  #applyZoomTransform() {
    if (!this.refs.zoomImage) return;
    this.refs.zoomImage.style.transform = `translate(${this.#zoomX}px, ${this.#zoomY}px) scale(${this.#zoomScale})`;
  }

  #bindZoomGestures() {
    const stage = this.refs.zoomStage;
    if (!stage) return;

    stage.addEventListener('pointerdown', (event) => {
      stage.setPointerCapture(event.pointerId);
      this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.#pointers.size === 2) {
        const [a, b] = Array.from(this.#pointers.values());
        this.#pinchStartDistance = Math.hypot(a.x - b.x, a.y - b.y);
        this.#pinchStartScale = this.#zoomScale;
      } else if (this.#pointers.size === 1 && this.#zoomScale > 1) {
        this.#panStart = { x: event.clientX - this.#zoomX, y: event.clientY - this.#zoomY };
      }
    });

    stage.addEventListener('pointermove', (event) => {
      if (!this.#pointers.has(event.pointerId)) return;
      this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.#pointers.size === 2) {
        const [a, b] = Array.from(this.#pointers.values());
        const distance = Math.hypot(a.x - b.x, a.y - b.y);
        if (this.#pinchStartDistance > 0) {
          this.#zoomScale = Math.max(1, Math.min(4, this.#pinchStartScale * (distance / this.#pinchStartDistance)));
          this.#applyZoomTransform();
        }
      } else if (this.#pointers.size === 1 && this.#panStart && this.#zoomScale > 1) {
        this.#zoomX = event.clientX - this.#panStart.x;
        this.#zoomY = event.clientY - this.#panStart.y;
        this.#applyZoomTransform();
      }
    });

    const endPointer = (event) => {
      this.#pointers.delete(event.pointerId);
      if (this.#pointers.size < 2) this.#pinchStartDistance = 0;
      if (this.#pointers.size === 0) {
        this.#panStart = null;
        if (this.#zoomScale <= 1.02) this.#resetZoomTransform();
      }
    };
    stage.addEventListener('pointerup', endPointer);
    stage.addEventListener('pointercancel', endPointer);

    // Double-tap / double-click to toggle 1x <-> 2x zoom.
    let lastTap = 0;
    stage.addEventListener('pointerup', (event) => {
      const now = Date.now();
      if (now - lastTap < 300 && this.#pointers.size === 0) {
        this.#zoomScale = this.#zoomScale > 1 ? 1 : 2;
        this.#zoomX = 0;
        this.#zoomY = 0;
        this.#applyZoomTransform();
      }
      lastTap = now;
    });

    // Wheel/trackpad pinch (ctrlKey on trackpad pinch gestures).
    stage.addEventListener(
      'wheel',
      (event) => {
        if (!event.ctrlKey) return;
        event.preventDefault();
        this.#zoomScale = Math.max(1, Math.min(4, this.#zoomScale - event.deltaY * 0.01));
        this.#applyZoomTransform();
      },
      { passive: false }
    );
  }
}

if (!customElements.get('offsure-gallery')) {
  customElements.define('offsure-gallery', OffsureGallery);
}
